import ClearIcon from '@mui/icons-material/Clear';
import CalendarIcon from '@mui/icons-material/InsertInvitation';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TextFieldProps } from '@mui/material/TextField';
import {
  DateTimePicker,
  type DateTimePickerProps,
  type DateTimeValidationError,
  type PickerChangeHandlerContext,
  PickersActionBar,
  type PickersActionBarProps,
  renderTimeViewClock,
} from '@mui/x-date-pickers';
import { DEFAULT_DATETIME_FORMAT, DEFAULT_DATETIME_FORMAT_PLACEHOLDER } from '@openx/constants/date.ts';
import { forwardRef, type JSX, useCallback, useId, useRef, useState } from 'react';
import {
  NextIconButton,
  PreviousIconButton,
  SwitchViewButton,
} from '../CalendarNavigationButtons/CalendarNavigationButtons.tsx';

const ACTION_BAR_CONTAINER_SX: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const TODAY_ACTION_BAR_SX: SxProps<Theme> = {
  justifyContent: 'flex-start',
  mb: 1,
  ml: 2,
  p: 0,
};

const getTodayValue = (): Date => {
  const localNow = new Date();
  const utcNow = new Date(localNow.getTime() + localNow.getTimezoneOffset() * 60_000);

  utcNow.setHours(0, 0, 0, 0);

  return utcNow;
};

type DateTimePickerActionBarProps = PickersActionBarProps & {
  onSetToday?: () => void;
};

const DateTimePickerActionBar = ({ className, onSetToday, ...props }: DateTimePickerActionBarProps): JSX.Element => (
  <Box className={className} sx={ACTION_BAR_CONTAINER_SX}>
    <Box sx={TODAY_ACTION_BAR_SX}>
      <Button onClick={onSetToday}>Today</Button>
    </Box>

    <PickersActionBar {...props} actions={['accept']} />
  </Box>
);

export type DateTimePickerFieldProps = Omit<DateTimePickerProps, 'format'> & {
  clearable?: boolean;
  'data-test'?: string;
  error?: TextFieldProps['error'];
  helperText?: TextFieldProps['helperText'];
  label?: TextFieldProps['label'];
  margin?: TextFieldProps['margin'];
  onBlur?: TextFieldProps['onBlur'];
  placeholder?: TextFieldProps['placeholder'];
  showTodayButton?: boolean;
  sx?: FormControlProps['sx'];
};

export const DateTimePickerField = forwardRef<HTMLInputElement, DateTimePickerFieldProps>(
  (
    {
      clearable,
      'data-test': dataTest,
      error,
      helperText,
      label,
      margin = 'none',
      onBlur,
      onChange,
      placeholder,
      showTodayButton = false,
      sx,
      value,
      ...rest
    },
    ref
  ): JSX.Element => {
    const id = useId();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const formControlRef = useRef<HTMLDivElement>(null);

    // The calendar popup can steal DOM focus (e.g. its "switch view" button) as soon as it opens,
    // which fires a native blur on the input even though the user hasn't left the field yet.
    // Skip validating in that case, and only do so once the popup has closed and focus has
    // genuinely moved outside the field, matching the behavior of plain text fields.
    const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
      if (isOpen) {
        return;
      }

      onBlur?.(event as unknown as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
    };

    const handleClose = () => {
      setIsOpen(false);

      // Closing the popup (e.g. via Escape) can restore focus back to the field's own input
      // synchronously by the time this callback runs, so check right away whether the user
      // genuinely left the field.
      if (!formControlRef.current?.contains(document.activeElement)) {
        onBlur?.({} as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
      }
    };

    const handleToday = useCallback(() => {
      const todayValue = getTodayValue();

      const changeContext = {
        validationError: null,
      } as PickerChangeHandlerContext<DateTimeValidationError>;

      onChange?.(todayValue, changeContext);
    }, [onChange]);

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.stopPropagation();

      const changeContext = {
        validationError: null,
      } as PickerChangeHandlerContext<DateTimeValidationError>;

      onChange?.(null, changeContext);
    };

    return (
      <FormControl data-test={dataTest} fullWidth margin={margin} ref={formControlRef} sx={sx}>
        {label ? (
          <FormLabel error={error} htmlFor={id}>
            {label}
          </FormLabel>
        ) : null}

        <DateTimePicker
          format={DEFAULT_DATETIME_FORMAT}
          inputRef={ref}
          onChange={onChange}
          onClose={handleClose}
          open={isOpen}
          slots={{
            ...(showTodayButton
              ? {
                  actionBar: DateTimePickerActionBar,
                }
              : {}),
            previousIconButton: PreviousIconButton,
            nextIconButton: NextIconButton,
            switchViewButton: SwitchViewButton,
          }}
          slotProps={{
            ...(showTodayButton
              ? {
                  actionBar: {
                    onSetToday: handleToday,
                  },
                }
              : {}),
            clearButton: {
              // @ts-expect-error there is no type definition for data-* attributes on clear button, although it works
              'data-test': 'clear-button',
            },
            field: {
              clearable,
            },
            textField: {
              error,
              helperText,
              onBlur: handleBlur,
              onClick: () => setIsOpen(true),
              variant: 'outlined',

              slotProps: {
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box
                        sx={{
                          mr: 1,
                        }}
                      >
                        UTC
                      </Box>

                      {clearable && value ? (
                        <IconButton aria-label="Clear" data-test="clear-button" onClick={handleClear} size="small">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ) : null}

                      <IconButton size="small">
                        <CalendarIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                },

                htmlInput: {
                  placeholder: placeholder ?? DEFAULT_DATETIME_FORMAT_PLACEHOLDER,
                  // @ts-expect-error there is no type definition for data-* attributes on this slot, although it works
                  'data-test': 'date-picker',
                  id,
                },
              },
            },
          }}
          value={value}
          viewRenderers={{
            hours: renderTimeViewClock,
            minutes: renderTimeViewClock,
          }}
          {...rest}
        />
      </FormControl>
    );
  }
);
